import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { homeApi } from '../api/home';
import BottomNav from '../components/BottomNav';
import Navbar from '../components/Navbar';
import './Chat.css';

const Chat = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  
  const [memberId, setMemberId] = useState(0);
  const [memberName, setMemberName] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(0);
  const [currentUserAvatar, setCurrentUserAvatar] = useState('');
  const [recipientAvatar, setRecipientAvatar] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputContent, setInputContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  useEffect(() => {
    const id = parseInt(searchParams.get('member_id') || '0');
    const name = searchParams.get('member_name') || '';
    const sid = searchParams.get('session_id') || null;
    
    setMemberId(id);
    setMemberName(decodeURIComponent(name));
    setSessionId(sid);
    setRecipientName(decodeURIComponent(name));
  }, [searchParams]);

  useEffect(() => {
    if (memberId > 0) {
      initChat();
    }
  }, [memberId]);

  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  const checkLoginStatus = () => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    if (!token) {
      navigate('/login');
      return false;
    }
    return true;
  };

  const initChat = async () => {
    if (!checkLoginStatus()) return;

    const token = localStorage.getItem('token');
    
    try {
      const userInfoResult = await homeApi.getUserInfo();
      
      if (userInfoResult.code === 200 || userInfoResult.code === 1) {
        const userInfo = userInfoResult.data?.userinfo || {};
        
        const userId = userInfo.id || userInfo.member_id || userInfo.user_id || 0;
        
        setCurrentUserId(userId);
        setCurrentUserAvatar(userInfo.avatar || userInfo.wechat_avatar || '/avatar.jpg');
        
        localStorage.setItem('userInfo', JSON.stringify(userInfo));

        if (memberId === userId) {
          alert('不能给自己发消息');
          navigate(-1);
          return;
        }

        await loadRecipientInfo();
        await loadMessages();
        startRefresh();
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      alert('获取用户信息失败');
      navigate(-1);
    }
  };

  const loadRecipientInfo = async () => {
    try {
      const result = await homeApi.getMemberInfo({ member_id: memberId });
      if (result.code === 200) {
        const member = result.data;
        setRecipientName(member.nick_name || member.contact_person || '未知用户');
        setRecipientAvatar(member.wechat_avatar || '/avatar.jpg');
      }
    } catch (error) {
      console.error('获取接收者信息失败:', error);
    }
  };

  const loadMessages = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      const requestData = {
        session_id: sessionId,
        recipient_id: memberId
      };

      const result = await homeApi.getMemberMessages(requestData);
      
      if (result.code === 200) {
        const newMessages = result.data || [];
        
        if (newMessages.length > 0) {
          const currentIds = messages.map(m => m.id);
          const addedMessages = newMessages.filter(m => !currentIds.includes(m.id));
          
          if (addedMessages.length > 0) {
            setMessages(newMessages);
          }
        }
      }
    } catch (error) {
      console.error('获取消息失败:', error);
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const content = inputContent.trim();
    if (!content || sending) return;

    if (memberId === currentUserId) {
      alert('不能给自己发消息');
      return;
    }

    setSending(true);

    const tempMessage = {
      id: Date.now(),
      session_id: sessionId,
      sender_id: parseInt(currentUserId),
      send_member_id: parseInt(currentUserId),
      recive_member_id: parseInt(memberId),
      content: content,
      nick_name: '我',
      wechat_avatar: currentUserAvatar,
      create_time: Math.floor(Date.now() / 1000)
    };

    setMessages(prev => [...prev, tempMessage]);
    setShouldScrollToBottom(true);

    const messageData = {
      content: content
    };

    if (sessionId) {
      messageData.session_id = sessionId;
    } else {
      messageData.recipient_id = memberId;
    }

    try {
      const result = await homeApi.sendMemberMessage(messageData);
      
      if (result.code === 200) {
        setInputContent('');
        if (!sessionId && result.data && result.data.session_id) {
          setSessionId(result.data.session_id);
        }

        const index = messages.findIndex(m => m.id === tempMessage.id);
        if (index !== -1 && result.data && result.data.message) {
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[index] = result.data.message;
            return newMessages;
          });
        }
      } else {
        alert(result.msg || '发送失败');
        
        setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      alert('发送失败');
      
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp * 1000);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    
    const weekAgo = new Date(today.getTime() - 7 * 86400000);
    
    if (messageDate.getTime() === today.getTime()) {
      return `${hours}:${minutes}:${seconds}`;
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return `昨天 ${hours}:${minutes}`;
    } else if (messageDate.getTime() > weekAgo.getTime()) {
      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
      return `${weekDays[date.getDay()]} ${hours}:${minutes}`;
    } else {
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day} ${hours}:${minutes}`;
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const startRefresh = () => {
    const interval = setInterval(() => {
      loadMessages();
    }, 3000);
    
    return () => clearInterval(interval);
  };

  const goBack = () => {
    navigate(-1);
  };

  const isSender = (item) => {
    const sendMemberId = parseInt(item.send_member_id) || 0;
    const currentUserIdNum = parseInt(currentUserId) || 0;
    
    return sendMemberId === currentUserIdNum;
  };

  if (loading) {
    return <div className="loading">加载中...</div>;
  }

  return (
    <div className="chat-page">
      <Navbar title={recipientName} showHome={false} />

      <div className="chat-container">
        <div className="message-list">
          {messages.length === 0 && (
            <div className="empty-message">
              <span className="icon">💬</span>
              <span className="empty-text">暂无消息，开始聊天吧</span>
            </div>
          )}
          
          {messages.map((item) => {
            const isSenderMsg = isSender(item);
            
            return (
              <div key={item.id}>
                <div className="message-time-wrapper">
                  <span className="message-time">{formatTime(item.create_time)}</span>
                </div>
                <div className={`message-item ${isSenderMsg ? 'message-send' : 'message-recive'}`}>
                  <img
                    className="message-avatar"
                    src={isSenderMsg ? currentUserAvatar : recipientAvatar}
                    alt="avatar"
                  />
                  <div className="message-content-wrapper">
                    {!isSenderMsg && (
                      <div className="message-nickname">
                        {item.nick_name || '未知用户'}
                      </div>
                    )}
                    <div className="message-content">{item.content}</div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-area">
          <div className="input-wrapper">
            <textarea
              className="message-input"
              value={inputContent}
              onChange={(e) => setInputContent(e.target.value)}
              placeholder="请输入消息..."
              maxLength={500}
              rows={1}
            />
            <div
              className={`send-btn ${!inputContent.trim() || sending ? 'disabled' : ''}`}
              onClick={sendMessage}
            >
              {sending ? '⏳' : '➤'}
            </div>
          </div>
        </div>
        <BottomNav activeTab="notification" />
      </div>
    </div>
  );
};

export default Chat;
